import React, { useState, useMemo } from 'react';
import { 
  CreditCard, PoundSterling, Search, RefreshCw, Loader2, 
  ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Clock,
  Calendar, Receipt, FileText, Eye, Plus, Settings
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../ui/collapsible';
import { cn } from '../../../lib/utils';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  deposit_paid: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  fully_paid: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

const statusLabels = {
  pending: 'Pending',
  deposit_paid: 'Deposit Paid',
  confirmed: 'Confirmed',
  fully_paid: 'Fully Paid',
  cancelled: 'Cancelled',
};

// Financial stat card
const StatCard = ({ title, value, subtitle, icon: Icon, variant = 'default' }) => {
  const variants = {
    default: 'text-slate-900',
    success: 'text-emerald-600',
    warning: 'text-orange-600',
    muted: 'text-slate-500',
  };
  
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-2 text-slate-500 mb-1">
        {Icon && <Icon className="w-4 h-4" />}
        <span className="text-sm">{title}</span>
      </div>
      <p className={cn("text-xl font-bold", variants[variant])}>{value}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
  );
};

export default function WeekenderPayments({ 
  event, 
  summaryData,
  bookings, 
  loading, 
  authToken,
  onRefresh,
  onBookingSelect,
  onRecordPayment
}) {
  const [activeTab, setActiveTab] = useState('outstanding');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('balance');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Financial summary from event summary API (authoritative)
  const financials = summaryData?.financials || {};
  const bookingsByStatus = summaryData?.bookings_by_status || {};
  const paymentMethods = summaryData?.payment_methods || {};

  // Calculate financial values using authoritative booking data
  const financialStats = useMemo(() => {
    const activeBookings = (bookings || []).filter(b => b.status !== 'cancelled');
    const totalRevenue = activeBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);
    const totalPaid = activeBookings.reduce((sum, b) => sum + (b.amount_paid || 0), 0);
    const totalOutstanding = activeBookings.reduce((sum, b) => {
      const outstanding = (b.total_amount || 0) - (b.amount_paid || 0);
      return sum + Math.max(0, outstanding);
    }, 0);
    
    const fullyPaidCount = activeBookings.filter(b => 
      b.total_amount > 0 && b.amount_paid >= b.total_amount
    ).length;
    const depositPaidCount = activeBookings.filter(b => 
      b.status === 'deposit_paid' || (b.amount_paid > 0 && b.amount_paid < b.total_amount)
    ).length;
    const pendingCount = activeBookings.filter(b => 
      b.amount_paid === 0 || b.status === 'pending'
    ).length;
    
    return {
      totalRevenue,
      totalPaid,
      totalOutstanding,
      fullyPaidCount,
      depositPaidCount,
      pendingCount
    };
  }, [bookings]);

  // Collect all payment records for history view
  const allPayments = useMemo(() => {
    const payments = [];
    (bookings || []).forEach(booking => {
      (booking.payment_records || []).forEach(payment => {
        payments.push({
          ...payment,
          booking_id: booking.booking_id,
          customer_name: booking.customer_name,
          customer_email: booking.customer_email
        });
      });
    });
    // Sort by date descending
    return payments.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
  }, [bookings]);

  // Filter bookings for outstanding balances view
  const filteredBookings = useMemo(() => {
    let filtered = [...(bookings || [])];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(b => 
        b.customer_name?.toLowerCase().includes(query) ||
        b.customer_email?.toLowerCase().includes(query) ||
        b.booking_id?.toLowerCase().includes(query)
      );
    }
    
    // Status filter
    if (statusFilter === 'outstanding') {
      filtered = filtered.filter(b => {
        const outstanding = (b.total_amount || 0) - (b.amount_paid || 0);
        return outstanding > 0 && b.status !== 'cancelled';
      });
    } else if (statusFilter === 'fully_paid') {
      filtered = filtered.filter(b => 
        b.total_amount > 0 && b.amount_paid >= b.total_amount
      );
    } else if (statusFilter === 'deposit_paid') {
      filtered = filtered.filter(b => b.status === 'deposit_paid');
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter(b => b.status === 'pending' && b.amount_paid === 0);
    } else if (statusFilter === 'payment_plan') {
      filtered = filtered.filter(b => b.has_payment_plan);
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'customer':
          aVal = a.customer_name || '';
          bVal = b.customer_name || '';
          break;
        case 'total':
          aVal = a.total_amount || 0;
          bVal = b.total_amount || 0;
          break;
        case 'paid':
          aVal = a.amount_paid || 0;
          bVal = b.amount_paid || 0;
          break;
        case 'balance':
          aVal = Math.max(0, (a.total_amount || 0) - (a.amount_paid || 0));
          bVal = Math.max(0, (b.total_amount || 0) - (b.amount_paid || 0));
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        default:
          aVal = a.booking_id;
          bVal = b.booking_id;
      }
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return filtered;
  }, [bookings, searchQuery, statusFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'balance' ? 'desc' : 'asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-3 h-3 ml-1" /> : 
      <ChevronDown className="w-3 h-3 ml-1" />;
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Payments</h2>
          <p className="text-sm text-slate-500">Financial overview and payment tracking</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
          className="text-slate-500 hover:text-slate-700"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-1.5" />
          )}
          Refresh
        </Button>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Total Revenue"
          value={formatCurrency(financials.total_revenue || financialStats.totalRevenue)}
          subtitle={`${bookings?.filter(b => b.status !== 'cancelled').length || 0} active bookings`}
          icon={PoundSterling}
        />
        <StatCard
          title="Amount Received"
          value={formatCurrency(financials.total_amount_paid || financialStats.totalPaid)}
          subtitle={`${financials.total_deposits_paid ? formatCurrency(financials.total_deposits_paid) + ' deposits' : ''}`}
          icon={CheckCircle}
          variant="success"
        />
        <StatCard
          title="Outstanding"
          value={formatCurrency(financials.total_balance_due || financialStats.totalOutstanding)}
          subtitle={`${financials.pending_payment_count || financialStats.pendingCount} pending`}
          icon={AlertTriangle}
          variant={financialStats.totalOutstanding > 0 ? "warning" : "muted"}
        />
        <StatCard
          title="Booking Status"
          value={`${financials.fully_paid_count || financialStats.fullyPaidCount} fully paid`}
          subtitle={`${financials.deposit_paid_count || financialStats.depositPaidCount} deposit paid`}
          icon={CreditCard}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-grid">
          <TabsTrigger value="outstanding" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Outstanding
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <Clock className="w-4 h-4" />
            Payment History
          </TabsTrigger>
        </TabsList>

        {/* Outstanding Balances Tab */}
        <TabsContent value="outstanding" className="mt-4">
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <PoundSterling className="w-4 h-4 text-violet-600" />
                Who Owes Money?
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    placeholder="Search by name, email, booking ref..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Bookings</SelectItem>
                    <SelectItem value="outstanding">Outstanding Only</SelectItem>
                    <SelectItem value="fully_paid">Fully Paid</SelectItem>
                    <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                    <SelectItem value="pending">Pending (Unpaid)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Count */}
              <div className="text-sm text-slate-500 mb-3">
                {filteredBookings.length} of {bookings?.length || 0} bookings
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-medium text-slate-600">BOOKING REF</th>
                      <th 
                        className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('customer')}
                      >
                        <span className="flex items-center">LEAD BOOKER <SortIcon field="customer" /></span>
                      </th>
                      <th 
                        className="text-right py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('total')}
                      >
                        <span className="flex items-center justify-end">TOTAL <SortIcon field="total" /></span>
                      </th>
                      <th 
                        className="text-right py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('paid')}
                      >
                        <span className="flex items-center justify-end">PAID <SortIcon field="paid" /></span>
                      </th>
                      <th 
                        className="text-right py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('balance')}
                      >
                        <span className="flex items-center justify-end">OUTSTANDING <SortIcon field="balance" /></span>
                      </th>
                      <th 
                        className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('status')}
                      >
                        <span className="flex items-center">STATUS <SortIcon field="status" /></span>
                      </th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-slate-500">
                          {searchQuery || statusFilter !== 'all' 
                            ? 'No bookings match your filters' 
                            : 'No bookings yet'}
                        </td>
                      </tr>
                    ) : filteredBookings.map((booking) => {
                      const total = booking.total_amount || 0;
                      const paid = booking.amount_paid || 0;
                      const balance = Math.max(0, total - paid);
                      
                      return (
                        <tr 
                          key={booking.booking_id} 
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                          onClick={() => onBookingSelect?.(booking)}
                        >
                          <td className="py-3 px-3">
                            <span className="font-mono text-xs text-slate-600">
                              {booking.booking_id?.slice(0, 15)}...
                            </span>
                            {booking.has_payment_plan && (
                              <Badge variant="outline" className="ml-2 text-xs">Plan</Badge>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            <div className="font-medium text-slate-900">{booking.customer_name}</div>
                            <div className="text-xs text-slate-500">{booking.customer_email}</div>
                          </td>
                          <td className="py-3 px-3 text-right font-medium text-slate-900">
                            {formatCurrency(total)}
                          </td>
                          <td className="py-3 px-3 text-right font-medium text-emerald-600">
                            {formatCurrency(paid)}
                          </td>
                          <td className="py-3 px-3 text-right">
                            <span className={cn(
                              "font-medium",
                              balance > 0 ? "text-orange-600" : "text-slate-400"
                            )}>
                              {formatCurrency(balance)}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <Badge className={cn("text-xs", statusColors[booking.status])}>
                              {statusLabels[booking.status] || booking.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onBookingSelect?.(booking);
                                }}
                                title="View booking"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {balance > 0 && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onRecordPayment?.(booking);
                                  }}
                                  title="Record payment"
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {filteredBookings.length === 0 ? (
                  <p className="text-center py-8 text-slate-500">
                    {searchQuery || statusFilter !== 'all' 
                      ? 'No bookings match your filters' 
                      : 'No bookings yet'}
                  </p>
                ) : filteredBookings.map((booking) => {
                  const total = booking.total_amount || 0;
                  const paid = booking.amount_paid || 0;
                  const balance = Math.max(0, total - paid);
                  
                  return (
                    <div 
                      key={booking.booking_id}
                      className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 cursor-pointer"
                      onClick={() => onBookingSelect?.(booking)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-slate-900">{booking.customer_name}</p>
                          <p className="text-xs text-slate-500 font-mono">{booking.booking_id?.slice(0, 15)}...</p>
                        </div>
                        <Badge className={cn("text-xs", statusColors[booking.status])}>
                          {statusLabels[booking.status] || booking.status}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <p className="text-slate-500 text-xs">Total</p>
                          <p className="font-medium">{formatCurrency(total)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">Paid</p>
                          <p className="font-medium text-emerald-600">{formatCurrency(paid)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">Due</p>
                          <p className={cn(
                            "font-medium",
                            balance > 0 ? "text-orange-600" : "text-slate-400"
                          )}>{formatCurrency(balance)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-violet-600" />
                Payment History
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {allPayments.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">
                  No payments recorded yet
                </p>
              ) : (
                <>
                  <div className="text-sm text-slate-500 mb-3">
                    {allPayments.length} payment{allPayments.length !== 1 ? 's' : ''} recorded
                  </div>
                  
                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 px-3 font-medium text-slate-600">DATE</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">BOOKING</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">CUSTOMER</th>
                          <th className="text-right py-2 px-3 font-medium text-slate-600">AMOUNT</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">METHOD</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">TYPE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allPayments.map((payment, idx) => (
                          <tr 
                            key={`${payment.payment_id}-${idx}`}
                            className="border-b border-slate-100 hover:bg-slate-50"
                          >
                            <td className="py-3 px-3 text-slate-600">
                              {formatDate(payment.created_at)}
                            </td>
                            <td className="py-3 px-3">
                              <span className="font-mono text-xs text-slate-600">
                                {payment.booking_id?.slice(0, 15)}...
                              </span>
                            </td>
                            <td className="py-3 px-3">
                              <div className="font-medium text-slate-900">{payment.customer_name}</div>
                            </td>
                            <td className="py-3 px-3 text-right font-medium text-emerald-600">
                              {formatCurrency(payment.amount)}
                            </td>
                            <td className="py-3 px-3">
                              <Badge variant="outline" className="capitalize">
                                {payment.payment_method || 'unknown'}
                              </Badge>
                            </td>
                            <td className="py-3 px-3 text-slate-600 capitalize">
                              {payment.payment_type || 'payment'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden space-y-3">
                    {allPayments.map((payment, idx) => (
                      <div 
                        key={`${payment.payment_id}-${idx}`}
                        className="border border-slate-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-slate-900">{payment.customer_name}</p>
                            <p className="text-xs text-slate-500">{formatDate(payment.created_at)}</p>
                          </div>
                          <span className="font-bold text-emerald-600">
                            {formatCurrency(payment.amount)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize text-xs">
                            {payment.payment_method || 'unknown'}
                          </Badge>
                          <span className="text-xs text-slate-500 capitalize">
                            {payment.payment_type || 'payment'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Advanced Financial Corrections */}
      <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
        <CollapsibleTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full justify-between text-slate-500 hover:text-slate-700 border border-dashed border-slate-200"
          >
            <span className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Advanced Financial Corrections
            </span>
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3">
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Administrative Actions</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Financial corrections such as direct amount adjustments, deposit overrides, 
                    and total recalculations carry higher risk and should be used carefully.
                  </p>
                  <p className="text-sm text-amber-700 mt-2">
                    To make financial corrections, open the booking via the booking drawer and 
                    use the Edit Booking function in the legacy admin view.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
